import { useEffect, useRef, useState } from "react";
import supabase from "../../supabaseClient";
import { TASK_FEEDBACK_OPTIONS, getTaskFeedbackLabel } from "../../constants/taskFeedback";
import TaskIcon from "../TaskIcon";

export default function StudentTaskModal({ student, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [feedbackTask, setFeedbackTask] = useState(null);
  const [feedbackValue, setFeedbackValue] = useState("");
  const [hasMoreTasksBelow, setHasMoreTasksBelow] = useState(false);
  const taskScrollRef = useRef(null);

  const swoosh = new Audio("/sounds/swoosh.mp3");
  const ting = new Audio("/sounds/ting.mp3");

  useEffect(() => {
    async function loadData() {
      const { data: tasksData } = await supabase
        .from("tasks")
        .select("*")
        .eq("teacher_id", student.teacher_id)
        .order("created_at", { ascending: true });

      const { data: groupRows } = await supabase
        .from("group_students")
        .select("group_id")
        .eq("student_id", student.id);

      const groupIds = (groupRows || []).map((row) => row.group_id);

      let groupAssignments = [];
      if (groupIds.length > 0) {
        const { data } = await supabase
          .from("task_assignments")
          .select("task_id")
          .in("group_id", groupIds);
        groupAssignments = data || [];
      }

      const assignedTaskIds = new Set(
        groupAssignments.map((row) => row.task_id)
      );

      const visibleTasks = (tasksData || []).filter(
        (task) =>
          task.audience !== "targeted" ||
          (task.audience === "targeted" && assignedTaskIds.has(task.id))
      );

      setTasks(visibleTasks);

      const { data: statusData } = await supabase
        .from("task_status")
        .select("*")
        .eq("student_id", student.id);

      const map = {};
      (statusData || []).forEach((status) => {
        map[status.task_id] = {
          completed: status.completed,
          feedback: status.feedback || "",
        };
      });

      setStatuses(map);
    }

    loadData();
  }, [student]);

  useEffect(() => {
    const element = taskScrollRef.current;
    if (!element) return;

    const updateScrollHint = () => {
      const hasOverflow = element.scrollHeight > element.clientHeight + 2;
      const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 8;
      setHasMoreTasksBelow(hasOverflow && !isNearBottom);
    };

    updateScrollHint();
    const observer = new ResizeObserver(updateScrollHint);
    observer.observe(element);
    return () => observer.disconnect();
  }, [tasks]);

  function getStatus(taskId) {
    return statuses[taskId] || { completed: false, feedback: "" };
  }

  async function persistTaskStatus(task, completed, feedback) {
    const nextStatus = {
      completed,
      feedback: completed ? feedback || "" : "",
    };
    const nextStatuses = { ...statuses, [task.id]: nextStatus };
    const previousStatuses = statuses;

    setStatuses(nextStatuses);

    const existing = await supabase
      .from("task_status")
      .select("*")
      .eq("student_id", student.id)
      .eq("task_id", task.id)
      .maybeSingle();

    const payload = {
      completed,
      feedback: completed && task.feedback_enabled ? feedback : null,
      updated_at: new Date().toISOString(),
    };

    const result = existing.data
      ? await supabase
          .from("task_status")
          .update(payload)
          .eq("id", existing.data.id)
      : await supabase.from("task_status").insert([
          {
            student_id: student.id,
            task_id: task.id,
            ...payload,
          },
        ]);

    if (result.error) {
      setStatuses(previousStatuses);
      return;
    }

    if (completed) {
      swoosh.volume = 0.9;
      playSafe(swoosh);
    }

    const requiredTasks = tasks.filter((item) => item.priority !== "optional");
    const wasAllDone =
      requiredTasks.length > 0 &&
      requiredTasks.every((item) => getStatus(item.id).completed);
    const allDone =
      requiredTasks.length > 0 &&
      requiredTasks.every((item) => nextStatuses[item.id]?.completed);

    if (allDone && !wasAllDone) {
      ting.volume = 1;
      playSafe(ting);
    }
  }

  function openFeedbackPrompt(task) {
    setFeedbackTask(task);
    setFeedbackValue(getStatus(task.id).feedback || "");
  }

  async function toggleTask(task) {
    const isCompleted = getStatus(task.id).completed;
    if (!isCompleted && task.feedback_enabled) {
      openFeedbackPrompt(task);
      return;
    }

    await persistTaskStatus(task, !isCompleted, "");
  }

  async function selectFeedback(option) {
    if (!feedbackTask) return;
    setFeedbackValue(option);
    await persistTaskStatus(feedbackTask, true, option);
    setFeedbackTask(null);
    setFeedbackValue("");
  }

  function playSafe(audio) {
    if (!audio) return;
    audio.play().catch(() => {});
  }

  function backdropClose(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <>
      <div className="ct-modal-backdrop" onClick={backdropClose}>
        <div className="ct-modal ct-student-modal">
          <button
            onClick={onClose}
            className="ct-close-button absolute right-4 top-4"
            aria-label="Sluiten"
          >
            <span aria-hidden="true">×</span>
          </button>

          <div className="ct-modal-header pr-10">
            <div>
              <h1 className="ct-modal-title">{student.student_name}</h1>
              <p className="ct-panel-subtitle">{tasks.length} taken</p>
            </div>
          </div>

          <div
            ref={taskScrollRef}
            className="ct-task-scroll space-y-3 pr-1"
            onScroll={(event) => {
              const element = event.currentTarget;
              setHasMoreTasksBelow(
                element.scrollTop + element.clientHeight < element.scrollHeight - 8
              );
            }}
          >
            {tasks.length === 0 && (
              <p className="text-gray-500 italic text-center">
                Geen taken beschikbaar.
              </p>
            )}

            {[...tasks]
              .sort((a, b) => (a.priority === "optional") - (b.priority === "optional"))
              .map((task) => {
                const status = getStatus(task.id);
                return (
                  <div
                    key={task.id}
                    className={`ct-task-row ct-student-task-row${task.priority === "optional" ? " is-optional" : ""}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`${task.title}, ${status.completed ? "voltooid" : "niet voltooid"}`}
                    onClick={(event) => {
                      if (event.target.closest("button")) return;
                      toggleTask(task);
                    }}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleTask(task);
                      }
                    }}
                  >
                    <button
                      type="button"
                      className={`ct-task-check ${status.completed ? "is-complete" : ""}`}
                      onClick={() => toggleTask(task)}
                      aria-label={status.completed ? `${task.title} niet klaar` : `${task.title} klaar`}
                      aria-pressed={status.completed}
                    >
                      <span className="ct-task-check-indicator">
                        {status.completed ? "✓" : ""}
                      </span>
                    </button>

                    <div className={`flex items-center gap-3 flex-1 min-w-0 ${status.completed ? "opacity-60" : ""}`}>
                      <span className="ct-task-icon"><TaskIcon value={task.icon} className="ct-task-svg" /></span>
                      <span className="ct-task-title">{task.title}</span>
                    </div>

                    <div className="ct-task-meta">
                      {task.samenwerken && <span title="Samenwerken">🤝</span>}
                      {task.zelf_nakijken && <span title="Zelf nakijken">🔑</span>}
                      {task.feedback_enabled && <span title="Feedback gevraagd">💬</span>}
                      {task.priority === "optional" && <span className="ct-badge is-optional">Magje</span>}
                    </div>

                    {task.feedback_enabled && status.completed && (
                      <div className="mt-2 ml-12 flex justify-end">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openFeedbackPrompt(task);
                          }}
                          className="text-sm font-semibold text-blue-600 hover:underline"
                        >
                          Feedback aanpassen
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {hasMoreTasksBelow && (
            <div className="ct-scroll-hint" aria-live="polite">
              <span aria-hidden="true">↓</span> Scroll voor meer taken
            </div>
          )}
        </div>
      </div>

      {feedbackTask && (
        <div className="ct-modal-backdrop z-[60]">
          <div className="ct-modal">
            <div className="ct-modal-header">
              <div>
                <p className="ct-eyebrow">Jouw feedback</p>
                <h2 className="ct-modal-title">Hoe ging deze taak?</h2>
                <p className="ct-panel-subtitle">{feedbackTask.title}</p>
              </div>
            </div>

            <div className="space-y-3">
              {TASK_FEEDBACK_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => selectFeedback(option)}
                  className={`ct-feedback-option ${feedbackValue === option ? "is-selected" : ""}`}
                >
                  {getTaskFeedbackLabel(option)}
                </button>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setFeedbackTask(null);
                  setFeedbackValue("");
                }}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
